import { Scan } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { useTimelinePlayback } from './TimelinePlaybackContext';

export interface ChartZoomToFitButtonProps {
  /** The chart's data extent as `[min, max]` epoch ms; the timeline zooms to exactly this window. */
  range: [number, number] | undefined;
  className?: string;
}

/**
 * Zoom-to-fit control shared by the CarePlan detail charts — rendered in each chart's header, it zooms
 * the shared timeline to the chart's own data range. Renders nothing unless a zoomable timeline is
 * mounted (the `requestZoom` action is present only from a real playback provider) and the chart has a
 * non-degenerate range to fit, so a chart shown without the timeline draws no orphaned button.
 */
export function ChartZoomToFitButton({ range, className }: ChartZoomToFitButtonProps): React.ReactNode {
  const { t } = useTranslation('records');
  const { requestZoom } = useTimelinePlayback();

  if (!requestZoom || !range) return null;

  const label = t('zoomTimelineToFit');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={label}
          className={cn('text-muted-foreground', className)}
          onClick={() => requestZoom({ start: new Date(range[0]), end: new Date(range[1]) })}
        >
          <Scan aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
